
import * as Sentry from "@sentry/vue";
import Vue from "vue";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { mapActions, mapGetters, mapMutations } from "vuex";
import isBeforeOrToday from "~/assets/utils/time/isBeforeOrToday";
import Modal from "~/components/Modal.vue";
import Notice from "~/components/Notice.vue";
import GuestLogin from "~/components/form/GuestLogin.vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import Login from "~/components/form/Login.vue";
import orderSummary from "~/components/ship/orderSummary.vue";
import { PickupOption } from "~/entities/Response/FormData";
import Validation from "~/entities/Validation";
import ValidationCosts from "~/entities/ValidationModules/ValidationCosts";
import dateFormatter from "~/assets/utils/time/dateFormatter";

const shipDateMachine = {
  0: "stage1ShipDate",
  1: "stage2ShipDate",
  2: "stage3ShipDate",
  3: "stage4ShipDate",
  4: "stage5ShipDate",
};

const deliverDateMachine = {
  0: "stage1DeliverDate",
  1: "stage2DeliverDate",
  2: "stage3DeliverDate",
  3: "stage4DeliverDate",
  4: "stage5DeliverDate",
};

export default Vue.extend({
  name: "ShipCosts",
  components: {
    LargeSelect,
    DatePicker,
    Notice,
    Login,
    GuestLogin,
    orderSummary,
    Modal,
  },
  layout: "WithServices",
  data() {
    return {
      stepNumber: 2,
      validation: new Validation(new ValidationCosts()),
      pickupDropOff: [],
      isOpen: false as boolean,
      isOpenResponsive: false as boolean,
      pickupDropOffSelected: [] as any,
      disableStartDate: new Date(),
      timePickerOptions: {},
      dateFormat: "MMM DD, YYYY",
      shipDates: [] as (string | null)[],
      Holidays: [] as any,
      currentYear: new Date().getFullYear() as number,
      activeForm: 0,
      loginModalTarget: null,
      modalOptions: null,
      loginModal: null,
      rendered: true as boolean,
    };
  },
  async fetch() {
    const _this = this as any;
    await _this.fetchPickupOptions();

    _this.pickupDropOff = _this.pickupOptions
      ?.map((el: PickupOption, index: number) => {
        return {
          id: index as number,
          hasPickup: el.hasPickup as number,
          code: (index +
            "_" +
            el.display?.replace(/[^A-Z0-9]+/gi, "_")) as string,
          label: el.display as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
  },
  computed: {
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
      loading: "loading",
      boxes: "ship/boxes",
      trips: "ship/trips",
      rates: "ship/rates",
      units: "ship/units",
      fees: "ship/fees",
      pickupOption: "ship/pickupOption",
      selectedRateSpeed: "ship/selectedRateSpeed",
      selectedRateSpeedIndex: "ship/selectedRateSpeedIndex",
      premiumProtection: "ship/premiumProtection",
      session: "user/session",
      currentUnitStore: "ship/units",
      currentEvent: "eventShipping/currentEvent",
      outShip: "ship/outShip",
      ratesAreValid: "ship/ratesAreValid",

      // formData
      pickupOptions: "formData/pickupOptions" as any,
    }),
  },
  watch: {
    fees: {
      deep: true,
      immediate: true,
      handler() {
        (this as any).validation.module.validateCosts();
      },
    },
  },
  async mounted() {
    if (this.rates && this.rates[0] && this.rates[0].length) {
      this.setLoading(true);
      await this.getLandedCost();

      this.Holidays = await this.getScheduledDates();
      this.setDefaultRateSpeed();
      this.setHasAdvancedInStep(true);
      if (this.rates && this.rates[0]) {
        for (let step = 0; step < this.rates[0].length; step++) {
          this.shipDates[step] = null;
          this.pickupDropOffSelected[step] = false;
          // const nullDate = new Date('0001-01-01T00:00:00').toISOString()
          const rateIndex = this.selectedRateSpeedIndex[step];
          // here set the ship date to the current date
          this.setRateShipDate({
            index: step,
            value: new Date(
              this.rates[0][step].rates[rateIndex].deliver
            ).toISOString(),
          });
          this.selectRateSpeed(step, 0);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.calculateSubTotal();
      this.validation.module.component = this;
      this.setLoading(false);
      if (this.outShip && this.currentEvent) {
        const today = new Date();

        for (let index = 0; index < this.trips.length; index++) {
          const shipEvent = this.currentEvent[shipDateMachine[index]];
          const deliverEvent = this.currentEvent[deliverDateMachine[index]];

          if (shipEvent && !isBeforeOrToday(new Date(shipEvent), true)) {
            if (isBeforeOrToday(new Date(shipEvent), true)) {
              this.setShipDate(
                this.convertEventDates(today.toISOString()),
                index
              );
            } else {
              this.setShipDate(this.convertEventDates(shipEvent), index);
            }
          } else if (
            deliverEvent &&
            !isBeforeOrToday(new Date(deliverEvent), true)
          ) {
            const currentRate = this.rates[0][index].rates;
            const highestRateTime = currentRate.reduce((prev, current) =>
              prev.transitDays > current.transitDays ? prev : current
            );
            // TODO: Check if this is the correct way to get the current delivery date
            // TODO: connect the new values of getRates here
            let currentDeliveryDate = new Date(
              (deliverEvent + "T00:00:01.001Z") as string
            );
            const deliverDays = highestRateTime.transitDays;
            const limitDate = new Date(
              (deliverEvent + "T00:00:01.001Z") as string
            );
            currentDeliveryDate.setDate(
              currentDeliveryDate.getDate() - deliverDays
            );
            // the for loop is used to check if there is a weekend day between the date obtained from the calculated date,
            // and the delivery deadline, to omit it
            for (let index = 1; index <= deliverDays; index++) {
              const futureDate = new Date(currentDeliveryDate.getTime());
              futureDate.setDate(futureDate.getDate() + index);
              if (futureDate.getDay() === 6 || futureDate.getDay() === 0) {
                const compareDate = new Date(currentDeliveryDate);
                compareDate.setDate(compareDate.getDate() + deliverDays);
                if (compareDate >= limitDate) {
                  currentDeliveryDate.setDate(
                    currentDeliveryDate.getDate() - 1
                  );
                }
              }
            }
            // check if the day is a weekend to avoid it
            if (
              currentDeliveryDate.getDay() === 6 ||
              currentDeliveryDate.getDay() === 0
            ) {
              currentDeliveryDate.setDate(
                currentDeliveryDate.getDate() - currentDeliveryDate.getDay() ===
                  6
                  ? 1
                  : 2
              );
            }
            if (isBeforeOrToday(new Date(currentDeliveryDate), true)) {
              currentDeliveryDate = new Date();
              currentDeliveryDate.setDate(currentDeliveryDate.getDate() + 1);
            }
            const month = currentDeliveryDate.getMonth() + 1;
            const day = "" + currentDeliveryDate.getDate();
            const year = currentDeliveryDate.getFullYear();
            const fullDate: string = [year, month, day].join("-");
            this.setShipDate(this.convertEventDates(fullDate), index);
          }
        }
      }
      this.rendered = false;
    } else {
      // rates unvalid, back to search 1
      this.setStep(1);
    }
    try {
      this.calculateSubTotal();
      // to prevent removing requote alert on cost landing
      const requoted =
        this.ratesAreValid?.event?.results === "LocationChangedRequote"
          ? "requoted"
          : ("" as string);
      (this as any).validation.module.validateCosts(requoted);
      Sentry.addBreadcrumb({
        message: "Threshold: costs.",
        data: { rates: JSON.stringify(this.rates) },
        category: "cost",
      });
    } catch (e) {
      this.rendered = true;
    }
    this.rendered = true;
    this.updateTripsFormattedAddressLabels();
    window.scrollTo(0, 0);
    if (typeof window !== "undefined" && window.gtag) {
      const route = this.$route.name;
      let event = null;
      if (
        route === "events-event" ||
        route === "event" ||
        route === "partners-event"
      ) {
        event = this.currentEvent?.name
          ? this.currentEvent.name
          : this.$route.params.event;
      }
      try {
        window.gtag("event", "page_impression", {
          pagePath: this.$route.path,
          pageTitle: event ? "Ship - " + event + " - Costs" : "Ship - Costs",
          visitorType: this.$auth.loggedIn ? "customer" : "guest",
        });
      } catch (e) {
        console.error(e);
      }
    }
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setBoxes: "ship/setBoxes",
      setTrips: "ship/setTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",
      setRates: "ship/setRates",
      setPickUpOption: "ship/setPickUpOption",
      setRateShipDate: "ship/setRateShipDate",
      setUPSDeliveryDate: "ship/setUPSDeliveryDate",
      setDeliveryDate: "ship/setDeliveryDate",
      setSelectedRateSpeed: "ship/setSelectedRateSpeed",
      setSelectedRateSpeedIndex: "ship/setSelectedRateSpeedIndex",
      setDefaultRateSpeed: "ship/setDefaultRateSpeed",
      cleanBoxErrors: "ship/cleanBoxErrors",
      getTrip: "ship/getTrip",
      updateTripsFormattedAddressLabels:
        "ship/updateTripsFormattedAddressLabels",
      setHasAdvancedInStep: "ship/setHasAdvancedInStep",
    }),
    ...mapActions({
      getRates: "ship/getRates",
      calculateSubTotal: "payment/calculateSubTotal",
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      setStep: "ship/setStep",
      getLandedCost: "ship/getLandedCost",
      logRequest: "ship/logRequest",
      getScheduledDates: "ship/getScheduledDates",
      getRateDates: "ship/getRateDates",
      // formData
      fetchPickupOptions: "formData/fetchPickupOptions",
    }),
    updateRates(value: any) {
      (this as any).setRates(value);
    },
    pickupSelectionMade(selection: any): any {
      (this as any).setPickUpOption({ selection, index: selection.name });
      (this as any).pickupDropOffSelected[selection.name] = true;
      // to prevent removing requote alert on cost landing
      const requoted =
        this.ratesAreValid?.event?.results === "LocationChangedRequote"
          ? "requoted"
          : ("" as string);
      (this as any).validation.module.validatePickupSelection(requoted);
      (this as any).calculateSubTotal();
    },
    /**
     * Sets the ship date for a specific stage in the shipping process, this function is triggered by the selection of a date in the date picker.
     *
     * @param {string} event - The date to set as the ship date. This should be a string representing a date.
     * @param {number} index - The index of the stage in the shipping process for which to set the ship date.
     *
     * This function first creates a new Date object from the provided date string, adjusting for timezone differences.
     * It then checks if the provided date is valid for the specified stage by calling the validateStageDate function.
     * If the date is not valid, it resets the ship date for all subsequent stages.
     * After that, it sets the ship date for the specified stage and updates the delivery dates.
     * Finally, it validates the pickup selection and recalculates the total cost.
     *
     * @throws {Error} Throws an error if the date conversion fails.
     */
    setShipDate(event: string, index: number): void {
      try {
        // TODO: Check if index is not null in any context / scope
        // Create a date type object, convert it to a string and then take the timezone portion
        const dateToday = new Date().toString();
        const stringTime = dateToday.slice(16, dateToday.length);
        // Create another event date object and convert it to a string to take the date portion.
        const dateEvent = new Date(event).toString();
        const stringDate = dateEvent.slice(0, 16);
        // Create the Date type object with the concatenation string Date+timezone
        const date = new Date(stringDate + stringTime);

        const rates = this.rates[0][index].rates;
        const bodyRequest: {
          actionDate: string;
          dateType: string;
          transitDays: any;
          upsTransitDays: any;
          shipSpeed: any;
        }[] = [];

        for (let i = 0; i < rates.length; i++) {
          const rate = {
            actionDate: dateFormatter(date),
            dateType: "S",
            transitDays: rates[i].transitDays,
            upsTransitDays: rates[i].upsTransitDays,
            shipSpeed: rates[i].shortName,
          };
          bodyRequest.push(rate);
        }
        if (this.validateStageDate(index)) {
          if (this.shipDates) {
            for (let i = index + 1; i < this.shipDates.length; i++) {
              if (this.shipDates && this.shipDates[i]) {
                this.shipDates[i] = null;
                const resetDate = new Date(-72000000);
                this.setRateShipDate({
                  value: resetDate.toISOString(),
                  index: i,
                });
              }
            }
          }
        }

        this.getRateDates({ bodyRequest, index });
        // this.setRateShipDate({ value: date.toISOString(), index })
        this.shipDates[index] = event;
        // this.setDeliveryDates()
        // to prevent removing re-quote alert on cost landing
        const requoted =
          this.ratesAreValid?.event?.results === "LocationChangedRequote"
            ? "requoted"
            : ("" as string);
        // TODO: Implement Template on the Class instead of polymorphism
        (this as any).validation.module.validatePickupSelection(requoted);
        this.calculateSubTotal();
      } catch (e) {
        console.log(e);
      }
    },
    validateStageDate(index: number): boolean {
      if (this.shipDates.length > 0 && this.shipDates[index + 1]) {
        if (
          typeof this.shipDates[index] === "string" ||
          typeof this.shipDates[index] === "number"
        ) {
          const stageCheckDate = new Date(String(this.shipDates[index]));
          for (let i = index + 1; i < this.shipDates.length; i++) {
            const otherStageDate = new Date(String(this.shipDates[i]));
            if (stageCheckDate.getTime() >= otherStageDate.getTime()) {
              return true;
            }
          }
        }
      }
      return false;
    },
    selectRateSpeed(index: number, rateIndex: number) {
      (this as any).rendered = false;
      (this as any).setSelectedRateSpeedIndex({ index, rateIndex });
      (this as any).setSelectedRateSpeed({ index, rateIndex });
      (this as any).calculateSubTotal();
      // (this as any).setDeliveryDates();
      // (this as any).$refs.orderSummary.reRender();
      (this as any).rendered = true;
    },
    setDeliveryDates() {
      // TODO: Check if here ISO/UTC is correct
      this.rendered = false;
      // const nullDate = new Date('0001-01-01T00:00:00Z').toISOString()
      // nullDate is used in the first load and if the ship date is not set
      if ((this as any).rates?.[0]) {
        for (let index = 0; index < (this as any).rates[0].length; index++) {
          const rateIndex = (this as any).selectedRateSpeedIndex[index];
          // const rateShipDate = this.rates[0][index].rates[rateIndex].ship ?? nullDate
          this.setUPSDeliveryDate({
            index,
            value: new Date(
              this.rates[0][index].rates[rateIndex].upsDeliver
            ).toISOString(),
          });
          this.setDeliveryDate({
            index,
            value: new Date(
              this.rates[0][index].rates[rateIndex].deliver
            ).toISOString(),
          });
        }
      }
      (this as any).rendered = true;
    },
    /**
     * Checks if the provided date is before or equal to today's date.
     *
     * @param {Date} dateInput - The date to check.
     * @param {boolean} [isEqual=false] - A flag indicating whether to consider the date as valid if it's equal to today's date. By default, it's set to false.
     * @returns {boolean} Returns true if the provided date is before (or equal, depending on the isEqual flag) to today's date. Otherwise, it returns false.
     * @throws {TypeError} Throws an error if the provided date input is not a Date object or a string representing a date.
     */
    isBeforeOrToday,
    formatPrices(value: any): any {
      return Number(parseFloat(value as string).toFixed(2)).toLocaleString(
        "en-US",
        {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }
      );
    },
    /**
     * Returns a function that determines if a given date should be disabled in the date picker.
     *
     * @param {number} index - The index of the current stage in the shipping process.
     *
     * @returns {Function} Returns a function that takes two parameters:
     * 1. calendarDate: The date to check.
     * 2. selectedDate: The currently selected date(s) in the date picker. This is an optional parameter and defaults to an empty array.
     * This function returns a boolean indicating whether the date should be disabled.
     *
     * The returned function calls the getDisabledDates method with the provided parameters and the index.
     */
    setDisabledDates(
      index: number
    ): (calendarDate: Date, selectedDate?: Date[]) => boolean {
      return (calendarDate: Date, selectedDate: Date[] = []) => {
        return this.getDisabledDates(calendarDate, selectedDate, index);
      };
    },
    /**
     * Determines if a given is disabled for shipping.
     *
     * @param {Date} calendarDate - The date to check.
     * @param {Date[]} [selectedDate=[]] - The currently selected date(s) in the date picker.
     * @param {number} [index=-1] - The index of the current stage in the shipping process.
     *
     * @returns {boolean} Returns true if the date should be disabled, false otherwise.
     *
     * The function checks the following conditions in order:
     * 1. If the date is a holiday, it should be disabled.
     * 2. If the date is more than a year in the future, it should be disabled for performance reasons.
     * 3. If the date is a weekend (Saturday or Sunday), it should be disabled.
     * 4. If the date is before today, it should be disabled.
     * 5. If there is a pickup fee and the date is today or before, it should be disabled.
     * 6. If there are ship dates and the index is not -1, the function checks the following conditions:
     *    a. If the date is already selected in another stage, it should be disabled.
     *    b. If the date is before the previous ship date and the previous ship date is not the currently selected date and the index is 0 (i.e., this is the first date picker), it should be disabled.
     */
    getDisabledDates(
      calendarDate: Date,
      selectedDate: Date[] = [],
      index: number = -1
    ): boolean {
      if (this.isADayOff(calendarDate)) {
        // Disable if is a Holiday
        return true;
      }
      // TODO: Check if this should be 12months or 1 year
      if (calendarDate.getFullYear() > this.currentYear + 1) {
        // Disable if a calendarDate is a year in the future to improve performance
        return true;
      }
      if (!(calendarDate.getDay() % 6)) {
        // Disable if is a weekend
        return true;
      }
      if (isBeforeOrToday(new Date(calendarDate), false)) {
        // Disable days before Today
        return true;
      }
      if (
        this.rates[0][0]?.pickupFee > 0 &&
        isBeforeOrToday(new Date(calendarDate), true)
      ) {
        // Disable if is a pickup and the day is today or before
        return true;
      }
      if (this.shipDates.length && index !== -1) {
        const previousSelectedDate = selectedDate?.[0] || null;
        for (let i = 0; i < this.shipDates.length; i++) {
          const shipDate = new Date(this.shipDates[i] || "");
          const previousShipDate = new Date(this.shipDates[i - 1] || "");
          // this for is used to check and avoid weekends between the ship date and the delivery date
          for (
            let index = 0;
            index < this.selectedRateSpeed[i].transitDays;
            index++
          ) {
            previousShipDate.setDate(previousShipDate.getDate() + 1);
            if (
              previousShipDate.getDay() === 6 ||
              previousShipDate.getDay() === 0
            ) {
              previousShipDate.setDate(
                previousShipDate.getDate() +
                  (previousShipDate.getDay() === 6 ? 2 : 1)
              );
            }
          }

          if (shipDate?.getTime() === calendarDate?.getTime() && index !== i) {
            // Disable if a date is already selected on another Stage and the Stage is not the same as the index
            return true;
          }

          if (
            i > 0 &&
            index > 0 &&
            calendarDate.getTime() < previousShipDate.getTime() &&
            previousShipDate.getTime() !== previousSelectedDate?.getTime()
          ) {
            // Disable if a date is before the previous Ship Date
            return true;
          }
        }
      }
      return false;
    },
    getNextAvailableDate(date: any): any {
      if (typeof date === "string" || date instanceof String)
        date = new Date(date as string);
      let invalid = false;
      let currentDate = date;
      let loops = 365;
      do {
        loops--;
        currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
        const isWeekend = !(currentDate.getDay() % 6);
        const isDayOff = (this as any).isADayOff(currentDate);
        invalid = isWeekend || isDayOff;
      } while (invalid && loops);
      return currentDate.toLocaleDateString("en-us", {
        year: "numeric",
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },
    isADayOff(date: Date): boolean {
      if (this.Holidays && this.Holidays.length > 0) {
        for (let index = 0; index < this.Holidays.length; index++) {
          const holiday = new Date(this.Holidays[index]);
          const parseHoliday = holiday.toISOString();
          const parseDate = date.toISOString();
          if (
            new Date(parseDate.split("T")[0]).getTime() ===
            new Date(parseHoliday.split("T")[0]).getTime()
          ) {
            return true;
          }
        }
      }
      return false;
    },
    async validateStep() {
      if (await (this as any).validation.Validate()) {
        if (
          (this as any).validation.module.loginModalCompleted ||
          ((this as any).session.user && (this as any).session.user.getID())
        ) {
          (this as any).logRequest(window ? window.navigator.userAgent : "");
          (this as any).setStep(3);
        } else {
          this.isOpen = true;
        }
      }
    },
    onSwitchForm(activeForm: number) {
      this.activeForm = activeForm;
    },
    hideModal() {
      this.isOpen = false;
    },
    modalConfirmationMade() {
      (this as any).validation.module.loginModalCompleted = true;
      if (this.isOpen) {
        this.isOpen = false;
        (this as any).logRequest(window ? window.navigator.userAgent : "");
        (this as any).setStep(3);
      }
    },
    /**
     * Converts a date string into a formatted string.
     *
     * @param {string} date - The date string to be converted.
     * @returns {string} Returns the date string formatted as 'MMM DD, YYYY'.
     *
     * @example
     * // returns "Jan 01, 2022"
     * convertEventDates("2022-01-01")
     */
    convertEventDates(date: string, needDay?: boolean): string {
      const body = {
        month: "short" as "short",
        day: "numeric" as "numeric",
        year: "numeric" as "numeric",
        timeZone: "UTC",
      };
      if (needDay) {
        const bodyExtend = { ...body, weekday: "short" as "short" };
        return new Date(date).toLocaleDateString("en-US", bodyExtend);
      }
      return new Date(date).toLocaleDateString("en-US", body);
    },
    showRate(rate: any, index: number): boolean {
      if (!rate) return false;
      const limit = this.convertEventDates(
        new Date(rate.deliver).toISOString()
      );
      const eventStartDate = this.currentEvent
        ? new Date(this.currentEvent.startDate)
        : null;

      if (
        rate &&
        (this as any).currentEvent[deliverDateMachine[index]] &&
        eventStartDate &&
        this.isEventStartDateValid(eventStartDate) &&
        this.currentEvent.typeKey !== 11
      ) {
        const eventStart = new Date(limit);
        return eventStartDate.getTime() > eventStart.getTime();
      }
      return true;
    },
    isEventStartDateValid(date): boolean {
      const dateObj = new Date(date);
      if (
        isNaN(dateObj.getTime()) ||
        dateObj.toISOString().startsWith("0001-01-01")
      ) {
        for (let i = 0; i < this.shipDates.length; i++) {
          if (!this.shipDates[i]) {
            return false;
          }
        }
      }
      return true;
    }
  },
  fetchOnServer: false,
});
